import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Sidebar } from './components/Sidebar';
import { Topbar } from './components/Topbar';
import { AuthGate } from './components/AuthGate';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { FloatingMirror } from './components/floating/FloatingMirror';
import { InstallDropZone } from './components/InstallDropZone';
import { InstallTasksDock } from './components/InstallTasksDock';
import { AutoswitchToasts } from './components/AutoswitchToasts';
import { TransportToasts } from './components/TransportToasts';
import { UpdateToast } from './components/UpdateToast';
import { WirelessPairBanner } from './components/WirelessPairBanner';
import { useAutoswitch } from './store/autoswitch';
import { useTransport } from './store/transport';
import Dashboard from './pages/Dashboard';
import Logs from './pages/Logs';
import Files from './pages/Files';
import Control from './pages/Control';
import Network from './pages/Network';
import Devices from './pages/Devices';
import DeviceDetail from './pages/DeviceDetail';
import Apps from './pages/Apps';
import Settings from './pages/Settings';
import Home from './pages/Home';
import { useDevice } from './store/device';
import { tools as devTools } from './tools/registry';
// DevTools playground shell pages — small, eagerly imported so rollup
// doesn't fan the lucide icon graph into a swarm of < 1 KB chunks. The
// actual tool pages (e.g. JsonTool) are still lazy via `tools/registry`.
import ToolboxOverview from './tools/pages/ToolboxOverview';
import CategoryView from './tools/pages/CategoryView';
import ToolPlaceholder from './tools/pages/ToolPlaceholder';

// recharts + xterm.js are heavy (≈ 260 KB gzipped between them) and only
// load when the user actually opens those pages. Lazy chunks keep the
// initial page (devices grid) snappy on slow links.
const Commands = lazy(() => import('./pages/Commands'));
const Performance = lazy(() => import('./pages/Performance'));
const StaticAnalysisLanding = lazy(() => import('./pages/static/StaticAnalysisLanding'));
const ApkWorkbench = lazy(() => import('./pages/static/ApkWorkbench'));
// fast-xml-parser + canvas overlay → lazy keeps the initial bundle lean.
const Inspector = lazy(() => import('./pages/Inspector'));

function LazyFallback({ label }: { label: string }) {
  return (
    <div className="flex h-full items-center justify-center gap-2 p-8 text-sm text-ink-muted">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>加载 {label} 中…</span>
    </div>
  );
}

function Padded({ children }: { children: React.ReactNode }) {
  return <div className="p-4">{children}</div>;
}

export default function App() {
  return (
    <Routes>
      {/*
        Phase 2.3 removed the username/password login screen. Every
        request now auto-issues a client-id cookie server-side so the
        SPA can mount directly into the device grid. `/login` bookmarks
        bounce to `/devices`; the optional "display name" picker lives
        as a settings panel inside AuthGate-protected pages.
      */}
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="*" element={<AuthGate><Shell /></AuthGate>} />
    </Routes>
  );
}

/**
 * Top-level chrome (sidebar + topbar + main scroll container). Renders
 * inside AuthGate so we can safely assume `user` is non-null in any of
 * the nested routes.
 */
function Shell() {
  // Re-render once on every location change so the `FloatingMirror` can
  // pick up the new serial without us threading it through prop-drilling.
  useLocation();
  // Open the auto-switch event WebSocket once per tab. The store
  // handles its own backoff reconnect on close, so unmounting the
  // shell (e.g. test teardown) is the only path that disconnects.
  const connectAutoswitch = useAutoswitch((s) => s.connect);
  // Dual-transport (transport-change + throughput-sample) — its own
  // WS, same lifecycle. Two separate streams keep the snapshot frames
  // small and let either side reconnect without disturbing the other.
  const connectTransport = useTransport((s) => s.connect);
  useEffect(() => {
    connectAutoswitch();
    connectTransport();
  }, [connectAutoswitch, connectTransport]);

  // When the server flips a record's active transport (USB ↔ Wi-Fi or
  // in/out of offline) drop the `['devices']` cache so the grid card
  // refetches `state` + `model` straight away rather than waiting on
  // its 5 s poll. Without this the user sees a stale "离线" badge AND
  // a stale thumbnail for up to 5 s after a USB unplug even though
  // the wireless leg is already live.
  const qc = useQueryClient();
  const transportChangeSeq = useTransport((s) => s.transportChangeSeq);
  useEffect(() => {
    if (transportChangeSeq === 0) return;
    void qc.invalidateQueries({ queryKey: ['devices'] });
  }, [transportChangeSeq, qc]);
  return (
    <div className="grid h-full grid-cols-[220px_1fr] grid-rows-[56px_1fr]">
      <div className="row-span-2 border-r border-line bg-bg-card">
        <Sidebar />
      </div>
      <Topbar />
      {/*
        `<main>` is the scroll container but has NO padding. Pages that don't
        need a sticky header sit inside `Padded` which adds `p-4`. Pages with
        a sticky header (Commands) opt out so their header can pin flush at
        `top: 0` of the scrollport.

        WirelessPairBanner mounts at the top of the scroll container so it
        stays visible regardless of which page the user is on; it self-
        hides when no device has `needsWirelessPair=true`.
      */}
      <main className="overflow-auto bg-bg">
        <WirelessPairBanner />
        <Routes>
          {/* Landing page: adaptive home that renders entry cards keyed
              to ADH availability. NO forced redirect — keeping `/` as a
              stable bookmark target means returning users always see the
              same screen, and offline-safe surfaces stay reachable when
              adh is down. See .cursor/rules/spa-and-adh-split.mdc. */}
          <Route path="/" element={<Home />} />
          <Route path="/devices" element={<Devices />} />
          {/* /agents removed — see docs/USB_AGENT.md for the rationale and
              how to re-enable when cross-machine USB sharing is needed. */}
          <Route path="/agents" element={<Navigate to="/devices" replace />} />
          {/* /batch, /admin, /registry removed by R3 cleanup — see
              docs/superpowers/specs/2026-06-01-devtools-homepage-design.md §4 */}
          <Route
            path="/static-analysis"
            element={
              <Suspense fallback={<LazyFallback label="静态分析" />}>
                <StaticAnalysisLanding />
              </Suspense>
            }
          />
          <Route
            path="/static-analysis/apk/:reportId?"
            element={
              <Suspense fallback={<LazyFallback label="APK 分析" />}>
                <ApkWorkbench />
              </Suspense>
            }
          />
          <Route path="/tools" element={<ToolboxOverview />} />
          <Route path="/tools/:category" element={<CategoryView />} />
          {devTools.map((t) =>
            t.Component ? (
              <Route
                key={t.path}
                path={t.path}
                element={
                  <Suspense fallback={<LazyFallback label={t.name} />}>
                    <t.Component />
                  </Suspense>
                }
              />
            ) : null,
          )}
          <Route path="/tools/:category/:toolId" element={<ToolPlaceholder />} />
          <Route
            path="/settings"
            element={
              <RouteErrorBoundary label="设置页">
                <Settings />
              </RouteErrorBoundary>
            }
          />
          {/*
            Legacy entry point. The "本机服务" page was folded into
            `/settings#device` along with the other settings tabs;
            keep this redirect so bookmarks + ServiceGate's offline
            fallback continue to land on the right surface.
          */}
          <Route path="/local-service" element={<Navigate to="/settings#device" replace />} />
          <Route path="/devices/:serial" element={<DeviceDetail />}>
            <Route index element={<Padded><Dashboard /></Padded>} />
            <Route
              path="commands"
              element={
                <Suspense fallback={<LazyFallback label="命令中心" />}>
                  <Commands />
                </Suspense>
              }
            />
            <Route
              path="perf"
              element={
                <Suspense fallback={<LazyFallback label="性能面板" />}>
                  <Performance />
                </Suspense>
              }
            />
            <Route
              path="inspector"
              element={
                <Suspense fallback={<LazyFallback label="布局检查" />}>
                  <Inspector />
                </Suspense>
              }
            />
            <Route path="apps" element={<Padded><Apps /></Padded>} />
            <Route path="network" element={<Padded><Network /></Padded>} />
            {/* Logs runs its own flex h-full + p-4 so the LogList viewport
                can claim every leftover pixel and produce a single inner
                scrollbar. Wrapping it in <Padded> truncates the h-full
                chain (Padded has no height), leaving a huge empty band
                below the list. */}
            <Route path="logs" element={<Logs />} />
            <Route path="files" element={<Padded><Files /></Padded>} />
            <Route path="control" element={<Padded><Control /></Padded>} />
          </Route>

          {/* Legacy bookmarks redirect to /devices/:serial/<sub> using the
              last-selected serial from the device store. When no serial is
              cached we bounce to /devices so the user picks one. */}
          <Route path="/dashboard" element={<LegacyRedirect sub="" />} />
          <Route path="/commands" element={<LegacyRedirect sub="commands" />} />
          <Route path="/perf" element={<LegacyRedirect sub="perf" />} />
          <Route path="/network" element={<LegacyRedirect sub="network" />} />
          <Route path="/logs" element={<LegacyRedirect sub="logs" />} />
          <Route path="/files" element={<LegacyRedirect sub="files" />} />
          <Route path="/control" element={<LegacyRedirect sub="control" />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {/*
        Mounted at app root (sibling of <Routes>) so route changes do NOT
        unmount it — the scrcpy WebSocket and WebCodecs decoder stay alive
        across pages.
      */}
      <FloatingMirror />
      {/*
        Global drag-and-drop install: listens to `window`-level capture
        events so a .apk / .xapk dropped on ANY part of the SPA installs
        to the active device. Mounted as a sibling of FloatingMirror so
        the overlay lives above the mirror window's z-stack.
      */}
      <InstallDropZone />
      {/*
        Bottom-right install task queue. Picks up tasks enqueued by
        `InstallDropZone` and survives navigation between pages so
        the user can drop a file on Page A, switch to Page B and
        still see the install progress / conflict prompt.
      */}
      <InstallTasksDock />
      {/* Floating toast region for USB → Wi-Fi standby provisioning results. */}
      <AutoswitchToasts />
      {/* Failover / online / offline notifications (transport-change). */}
      <TransportToasts />
      {/* PWA service-worker update + offline-ready prompts. */}
      <UpdateToast />
    </div>
  );
}

/**
 * Bridge for stale bookmarks like `/commands`. Reads the current serial
 * from the global device store (populated either by an earlier visit to a
 * device detail page or by the bookmark itself having a `?serial=` query
 * param) and rewrites the URL into the new nested form. No serial yet →
 * back to the devices grid.
 */
function LegacyRedirect({ sub }: { sub: string }) {
  // Allow `?serial=` overrides so external links can deep-link without
  // touching the store first.
  const location = useLocation();
  const qsSerial = new URLSearchParams(location.search).get('serial') ?? undefined;
  const storeSerial = useDevice((s) => s.serial);
  // useParams returns nothing for top-level legacy paths but we accept any
  // shape defensively.
  const params = useParams<{ serial?: string }>();
  const serial = params.serial || qsSerial || storeSerial;
  if (!serial) return <Navigate to="/devices" replace />;
  const target = sub
    ? `/devices/${encodeURIComponent(serial)}/${sub}`
    : `/devices/${encodeURIComponent(serial)}`;
  return <Navigate to={target} replace />;
}
